import { useMemo } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { setResourceOrder } from "@/lib/dexie/resourceOrdering";
import type { SchemaNames } from "instantdb/types";
import type { Resource } from "@/lib/types";
import { db, type ResourceOrderEntry } from "@/lib/dexie/dexieDB";

// Base resource interface for the minimum requirements
interface UseResourceOrderProps {
  slug?: SchemaNames;
  resources?: Resource[];
}

interface UseResourceOrderReturn<T extends Resource> {
  resourceOrdering: ResourceOrderEntry | undefined;
  orderedResources: T[];
  updateOrder: (resources: T[]) => void;
  isLoading: boolean;
}

export function useResourceOrder<T extends Resource = Resource>({
  slug,
  resources = [],
}: UseResourceOrderProps): UseResourceOrderReturn<T> {
  // Get resource ordering data from IndexedDB
  const resourceOrdering = useLiveQuery(() => {
    if (slug) {
      return db.resourceOrdering.get(slug);
    }
    return undefined;
  }, [slug]);

  // Parse the ordering data to create a map of ID -> order position
  const orderMap = useMemo(() => {
    if (!resourceOrdering?.value) return {};

    try {
      const parsedOrder = JSON.parse(resourceOrdering.value);

      if (Array.isArray(parsedOrder)) {
        // Convert array of [id, order] pairs to an object { id: order }
        return Object.fromEntries(parsedOrder);
      }

      if (typeof parsedOrder === "object" && parsedOrder !== null) {
        // Assume it's already an object { id: order }
        return parsedOrder;
      }

      console.warn("Unexpected format for order data:", parsedOrder);
      return {}; // Return empty if format is unexpected
    } catch (error) {
      console.error("Error parsing order data:", error);
      return {}; // Return empty on parsing error
    }
  }, [resourceOrdering?.value]);

  // Sort resources based on the orderMap, but only if DB is ready
  const orderedResources = useMemo(() => {
    // If no resources, return empty array
    if (!resources.length) {
      return resources as T[];
    }

    // If no order data exists, return the original resources
    if (!Object.keys(orderMap).length) {
      return resources as T[];
    }

    // Sort resources according to orderMap
    return [...resources].sort((a, b) => {
      const orderA = orderMap[a.id] ?? Number.MAX_SAFE_INTEGER;
      const orderB = orderMap[b.id] ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    }) as T[];
  }, [resources, orderMap]);

  // Save the new order to IndexedDB
  const updateOrder = (resources: T[]) => {
    const orderMap = new Map();
    resources.forEach((item, index) => {
      orderMap.set(item.id, index);
    });

    if (slug) {
      setResourceOrder(slug, orderMap);
    }
  };

  return {
    resourceOrdering,
    orderedResources,
    updateOrder,
    isLoading: false,
  };
}
